/**
 * <silo-coupon-cta [data-start] [data-redirect-after]>
 *   <a href="/products/…">Get $100 off</a>
 * </silo-coupon-cta>
 *
 * Coupon-aware CTA. No coupon yet → opens the lead popup (at the offer step, unless
 * `data-start` requests the quiz from the beginning). Already holds a deal — or the popup
 * is unavailable / JS is off — → the inner <a> just navigates (its href IS the product
 * page, the safe fallback). Coupon state comes from the SSOT (window.siloHoldsDeal =
 * helpers.js holdsDeal), never re-derived.
 *
 * `data-redirect-after`: once the popup (or savings wizard) this CTA opened is dismissed —
 * whether the visitor submitted the form or just closed it — navigate to the inner link's
 * href (the product page). One MutationObserver watches the element's class for its
 * "closed" signal, which covers every close path (X, overlay, Esc, auto-close after
 * submit). The live popup has no such signal, so nothing is watched for it.
 *
 * Performance:
 *  - Zero work until a click: one listener attached on connect, nothing else.
 *  - The element IS its own listener (handleEvent) — no bound closures, no per-instance
 *    allocations, and add/removeEventListener match cleanly.
 *  - Listener and observer released on disconnect → leak-free across theme-editor /
 *    section re-renders.
 */
if (!customElements.get('silo-coupon-cta')) {
  const popupClosed = (el) => el.classList.contains('t-hidden'); // PopupManager re-adds it after the close transition
  const wizardClosed = (el) => !el.classList.contains('is-open'); // the wizard drops it on every close path

  class SiloCouponCta extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.stopWatching();
    }

    handleEvent(event) {
      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link)) return;

      const holdsDeal = typeof window.siloHoldsDeal === 'function' && window.siloHoldsDeal();
      const popup = window.siloPopupManager;
      const central = typeof window.siloOpenPopup === 'function';

      // Has a coupon, or no popup to open → let the link navigate to the product page.
      if (holdsDeal || (!central && (!popup || typeof popup.open !== 'function'))) return;

      event.preventDefault();
      // Centralized, as the 'build' trigger source: the "Build-your-Silo
      // button opens" setting routes this CTA (wizard / ROI / live / page rules / nothing).
      const kind = central ? window.siloOpenPopup('coupon_cta', 'build') : (popup.open(), 'popup');
      if (kind === null) {
        // Routed to "Nothing" -> behave as a plain link to the product page.
        window.location.href = link.href;
        return;
      }
      if (kind === 'popup' && !this.hasAttribute('data-start') && popup && typeof popup.skipToStep4 === 'function') {
        popup.skipToStep4();
      }
      if (!this.hasAttribute('data-redirect-after')) return;
      if (kind === 'popup') {
        this.redirectWhenClosed(popup && popup.popupManager && popup.popupManager.popup, popupClosed, link.href);
      } else if (kind === 'wizard') {
        this.redirectWhenClosed(document.querySelector('savings-wizard'), wizardClosed, link.href);
      }
      // 'live' (and any future kind): no close signal to watch — the link's href is the fallback.
    }

    // Watch `el`'s class attribute until `isClosed(el)` holds, then navigate to `href`.
    // One observer per element at a time; released on close or on disconnect.
    redirectWhenClosed(el, isClosed, href) {
      if (!el || this._observer) return;
      this._observer = new MutationObserver(() => {
        if (!isClosed(el)) return;
        this.stopWatching();
        window.location.assign(href);
      });
      this._observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    }

    stopWatching() {
      if (!this._observer) return;
      this._observer.disconnect();
      this._observer = null;
    }
  }

  customElements.define('silo-coupon-cta', SiloCouponCta);
}
